import { useCallback, useSyncExternalStore } from 'react';

export type RemixCancellationScope = 'outer-lifecycle' | 'adjustment-request';

export type RemixCancellationIntent = {
	readonly sessionId: string;
	readonly conversationId: string;
	readonly scope: RemixCancellationScope;
	readonly adjustmentId?: string;
};

export type RemixCancellationDiagnosticKind =
	| 'adjustment-stop'
	| 'contradictory'
	| 'duplicate'
	| 'missing-origin'
	| 'stale';

export type RemixCancellationDiagnostic = {
	kind: RemixCancellationDiagnosticKind;
	phase?: 'thinking' | 'ready' | 'previewing' | 'inserting' | 'error' | 'aborted';
	scope?: RemixCancellationScope;
};

export type RemixCancellationSignal = RemixCancellationIntent;

export type RemixCancellationSnapshot = {
	readonly sessionId?: string;
	readonly conversationId?: string;
	readonly isObservationReady?: boolean;
	readonly pendingSignals: readonly RemixCancellationSignal[];
};

type DiagnosticReporter = (diagnostic: RemixCancellationDiagnostic) => void;

type ActiveSession = {
	readonly sessionId: string;
	readonly conversationId: string;
	readonly outerCancellationIntent: RemixCancellationIntent;
	readonly isObservationReady: boolean;
	readonly activeAdjustmentId?: string;
	readonly pendingSignals: readonly RemixCancellationSignal[];
	readonly hasReportedMissingOrigin: boolean;
	readonly reportDiagnostic?: DiagnosticReporter;
};

type RemixCancellationSessionAddress = {
	sessionId: string;
	conversationId: string;
};

const EMPTY_SNAPSHOT: RemixCancellationSnapshot = Object.freeze({
	pendingSignals: Object.freeze([]),
});

let nextIdentity = 0;
let activeSession: ActiveSession | undefined;
const subscribers = new Set<() => void>();

const nextId = (prefix: 'session' | 'adjustment'): string => {
	nextIdentity += 1;
	return `remix-cancellation-${prefix}-${nextIdentity}`;
};

const notifySubscribers = (): void => {
	subscribers.forEach((subscriber) => {
		try {
			subscriber();
		} catch {
			// Shadow observation must never affect the authoritative legacy flow.
		}
	});
};

const safelyReport = (
	reporter: DiagnosticReporter | undefined,
	diagnostic: RemixCancellationDiagnostic,
): void => {
	try {
		reporter?.(diagnostic);
	} catch {
		// Shadow diagnostics must never affect the authoritative legacy flow.
	}
};

export const registerRemixCancellationSession = ({
	conversationId,
	reportDiagnostic,
}: {
	conversationId: string;
	reportDiagnostic?: DiagnosticReporter;
}): string | undefined => {
	if (activeSession) {
		return undefined;
	}

	const sessionId = nextId('session');
	const outerCancellationIntent: RemixCancellationIntent = Object.freeze({
		sessionId,
		conversationId,
		scope: 'outer-lifecycle',
	});
	activeSession = {
		sessionId,
		conversationId,
		outerCancellationIntent,
		isObservationReady: false,
		pendingSignals: EMPTY_SNAPSHOT.pendingSignals,
		hasReportedMissingOrigin: false,
		reportDiagnostic,
	};
	notifySubscribers();
	return sessionId;
};

export const unregisterRemixCancellationSession = (sessionId: string): void => {
	if (activeSession?.sessionId !== sessionId) {
		return;
	}
	activeSession = undefined;
	notifySubscribers();
};

export const activateRemixCancellationObservation = ({
	conversationId,
	sessionId,
}: RemixCancellationSessionAddress): boolean => {
	if (
		!activeSession ||
		activeSession.conversationId !== conversationId ||
		activeSession.sessionId !== sessionId
	) {
		return false;
	}

	activeSession = { ...activeSession, isObservationReady: true };
	notifySubscribers();
	return true;
};

export const captureRemixCancellationIntent = ({
	conversationId,
}: {
	conversationId: string;
	scope: 'outer-lifecycle';
}): RemixCancellationIntent | undefined => {
	if (activeSession?.conversationId !== conversationId) {
		return undefined;
	}

	return activeSession.outerCancellationIntent;
};

export const startRemixAdjustment = (
	conversationId: string,
): RemixCancellationIntent | undefined => {
	if (activeSession?.conversationId !== conversationId) {
		return undefined;
	}

	const adjustmentId = nextId('adjustment');
	activeSession = { ...activeSession, activeAdjustmentId: adjustmentId };
	notifySubscribers();
	return Object.freeze({
		sessionId: activeSession.sessionId,
		conversationId,
		scope: 'adjustment-request',
		adjustmentId,
	});
};

export const recordRemixCancellation = ({
	sessionId,
	conversationId,
	scope,
	adjustmentId,
}: RemixCancellationIntent): RemixCancellationSignal | undefined => {
	const session = activeSession;
	if (!session || session.sessionId !== sessionId || session.conversationId !== conversationId) {
		return undefined;
	}

	if (
		scope === 'adjustment-request' &&
		(!adjustmentId || adjustmentId !== session.activeAdjustmentId)
	) {
		safelyReport(session.reportDiagnostic, { kind: 'stale', scope });
		return undefined;
	}

	const duplicate = session.pendingSignals.find(
		(signal) => signal.scope === scope && signal.adjustmentId === adjustmentId,
	);
	if (duplicate) {
		safelyReport(session.reportDiagnostic, { kind: 'duplicate', scope });
		return duplicate;
	}

	const signal: RemixCancellationSignal = Object.freeze({
		sessionId,
		conversationId,
		scope,
		...(adjustmentId ? { adjustmentId } : {}),
	});
	activeSession = { ...session, pendingSignals: [...session.pendingSignals, signal] };
	notifySubscribers();
	return signal;
};

export const claimNextRemixCancellation = ({
	sessionId,
	conversationId,
}: RemixCancellationSessionAddress): RemixCancellationSignal | undefined => {
	const session = activeSession;
	if (!session || session.sessionId !== sessionId || session.conversationId !== conversationId) {
		return undefined;
	}

	const [signal, ...pendingSignals] = session.pendingSignals;
	if (!signal) {
		return undefined;
	}

	activeSession = { ...session, pendingSignals };
	notifySubscribers();
	return signal;
};

export const reportRemixCancellationDiagnostic = ({
	sessionId,
	conversationId,
	diagnostic,
}: RemixCancellationSessionAddress & {
	diagnostic: RemixCancellationDiagnostic;
}): void => {
	if (activeSession?.sessionId !== sessionId || activeSession.conversationId !== conversationId) {
		return;
	}
	safelyReport(activeSession.reportDiagnostic, diagnostic);
};

export const reportMissingRemixCancellationOriginOnce = ({
	sessionId,
	conversationId,
	phase,
}: RemixCancellationSessionAddress & {
	phase: RemixCancellationDiagnostic['phase'];
}): void => {
	const session = activeSession;
	if (
		!session ||
		session.sessionId !== sessionId ||
		session.conversationId !== conversationId ||
		session.hasReportedMissingOrigin
	) {
		return;
	}

	activeSession = { ...session, hasReportedMissingOrigin: true };
	notifySubscribers();
	safelyReport(session.reportDiagnostic, { kind: 'missing-origin', phase });
};

export const getRemixCancellationSnapshot = ({
	sessionId,
	conversationId,
}: RemixCancellationSessionAddress): RemixCancellationSnapshot => {
	const session = activeSession;
	if (!session || session.sessionId !== sessionId || session.conversationId !== conversationId) {
		return EMPTY_SNAPSHOT;
	}
	return session;
};

export const subscribeToRemixCancellationSignals = (subscriber: () => void): (() => void) => {
	subscribers.add(subscriber);
	return () => subscribers.delete(subscriber);
};

export const useRemixCancellationSnapshot = ({
	sessionId,
	conversationId,
}: RemixCancellationSessionAddress): RemixCancellationSnapshot => {
	const getSnapshot = useCallback(
		() => getRemixCancellationSnapshot({ sessionId, conversationId }),
		[conversationId, sessionId],
	);
	return useSyncExternalStore(subscribeToRemixCancellationSignals, getSnapshot, getSnapshot);
};

export const clearRemixCancellationSignalsForTesting = (): void => {
	activeSession = undefined;
	nextIdentity = 0;
	subscribers.clear();
};
